import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Cpu, FileText, LifeBuoy, User, LogOut, Users, FilePen, UsersRound,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Logo } from "@/components/site/Logo";

export const PortalLayout = ({ children, admin = false }) => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const investorLinks = [
    { to: "/portal",              icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/portal/subscription", icon: FilePen,         label: "Subscription" },
    { to: "/portal/documents",    icon: FileText,        label: "Documents" },
    { to: "/portal/simulator",    icon: Cpu,             label: "Simulator" },
    { to: "/portal/team",         icon: UsersRound,      label: "Team" },
    { to: "/portal/support",      icon: LifeBuoy,        label: "Support" },
    { to: "/portal/profile",      icon: User,            label: t("portal.profile") },
  ];

  const adminLinks = [
    { to: "/admin",         icon: LayoutDashboard, label: t("admin.overview"), end: true },
    { to: "/admin/users",   icon: Users,           label: "Client accounts" },
    { to: "/admin/contact", icon: User,            label: t("admin.contactMsgs") },
    { to: "/admin/audit",   icon: FileText,        label: t("admin.audit") },
  ];

  const links = admin ? adminLinks : investorLinks;

  return (
    <ThemeProvider forceScope="dark">
      <div className="min-h-screen flex bg-background text-foreground">
        <aside data-testid="portal-sidebar" className="hidden lg:flex flex-col w-64 border-r border-border bg-card">
          <Link to="/" className="h-20 flex items-center px-6 border-b border-border">
            <Logo theme="light" size={28} />
          </Link>
          <div className="flex-1 overflow-y-auto px-3 py-6">
            <p className="overline text-muted-foreground px-3 mb-4">{admin ? t("admin.title") : t("portal.dashboard")}</p>
            <nav className="space-y-1">
              {links.map(l => (
                <NavLink
                  key={l.to} to={l.to} end={l.end}
                  data-testid={`portal-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-[13px] transition-colors ${
                      isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    }`
                  }
                >
                  <l.icon className="w-4 h-4" />
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 border border-border flex items-center justify-center font-mono text-[13px]">
                {(user?.full_name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-[13px] truncate">{user?.full_name || user?.email}</p>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{user?.role}</p>
              </div>
            </div>
            <button
              data-testid="portal-logout"
              onClick={() => { logout(); navigate("/"); }}
              className="w-full flex items-center justify-center gap-2 border border-border py-2 text-[12px] uppercase tracking-widest hover:bg-secondary transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> {t("common.logout")}
            </button>
          </div>
        </aside>

        <div className="flex-1 flex flex-col min-w-0">
          <div className="lg:hidden border-b border-border h-16 flex items-center justify-between px-4 bg-card">
            <Link to="/" className="flex items-center">
              <Logo theme="light" size={22} />
            </Link>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="portal-mobile-logout" className="text-[11px] uppercase tracking-widest text-muted-foreground">
              {t("common.logout")}
            </button>
          </div>
          <main className="flex-1 p-6 md:p-10 overflow-x-hidden">{children}</main>
        </div>
      </div>
    </ThemeProvider>
  );
};
